import React, { useState, useEffect } from 'react';
import Login from './components/Login';
import Dashboard from './components/Dashboard';
import OrdersList from './components/OrdersList';
import CurrentOrder from './components/CurrentOrder';
import DeliveryHistory from './components/DeliveryHistory';
import Profile from './components/Profile';
import ChatModal from './components/ChatModal';
import LocationPermission from './components/LocationPermission';
import { partnerAPI } from './services/api';
import './App.css';

type View = 'login' | 'dashboard' | 'orders' | 'current-order' | 'history' | 'profile';

function App() {
  const [locationGranted, setLocationGranted] = useState(false);
  const [partnerLocation, setPartnerLocation] = useState<{ lat: number; lng: number } | null>(null);
  const [view, setView] = useState<View>('login');
  const [partner, setPartner] = useState<any>(null);
  const [earnings, setEarnings] = useState<any>({});
  const [availableOrders, setAvailableOrders] = useState<any[]>([]);
  const [currentOrder, setCurrentOrder] = useState<any>(null);
  const [deliveryHistory, setDeliveryHistory] = useState<any[]>([]);
  const [newOrderPopup, setNewOrderPopup] = useState<any>(null);
  const [availableOrdersCount, setAvailableOrdersCount] = useState(0);
  const [chatModal, setChatModal] = useState<{ orderId: string, userName: string, phone: string } | null>(null);

  const handleLocationGranted = (location: { lat: number; lng: number }) => {
    setPartnerLocation(location);
    setLocationGranted(true);
  };

  useEffect(() => {
    const token = localStorage.getItem('partnerToken');
    if (token) {
      loadPartnerData();
    }
    
    // Request notification permission
    if ('Notification' in window && Notification.permission === 'default') {
      Notification.requestPermission();
    }
  }, []);

  // Poll for new orders when partner is available and on dashboard
  useEffect(() => {
    if (!partner || !partner.isAvailable || view !== 'dashboard') return;

    const checkForNewOrders = async () => {
      try {
        const response = await partnerAPI.getAvailableOrders();
        const newOrders = response.data;
        setAvailableOrdersCount(newOrders.length);

        // Check if there's a new order (not seen before)
        if (newOrders.length > 0 && availableOrdersCount < newOrders.length) {
          const latestOrder = newOrders[0]; // Get the newest order
          setNewOrderPopup(latestOrder);
        }
      } catch (error) {
        console.error('Failed to check for new orders:', error);
      }
    };

    // Check immediately
    checkForNewOrders();

    // Then check every 10 seconds
    const interval = setInterval(checkForNewOrders, 10000);

    return () => clearInterval(interval);
  }, [partner, view, availableOrdersCount]);

  // Poll for orders when viewing the orders list
  useEffect(() => {
    if (!partner || view !== 'orders') return;

    const refreshOrdersList = async () => {
      try {
        const response = await partnerAPI.getAvailableOrders();
        setAvailableOrders(response.data);
        setAvailableOrdersCount(response.data.length);
      } catch (error) {
        console.error('Failed to refresh orders list:', error);
      }
    };

    // Refresh every 5 seconds when viewing orders
    const interval = setInterval(refreshOrdersList, 5000);

    return () => clearInterval(interval);
  }, [partner, view]);

  const loadPartnerData = async () => {
    try {
      const [profileRes, earningsRes] = await Promise.all([
        partnerAPI.getProfile(),
        partnerAPI.getEarnings(),
      ]);
      
      setPartner(profileRes.data);
      setEarnings(earningsRes.data);
      setView('dashboard');
      
      // Load current order if exists
      try {
        const orderRes = await partnerAPI.getCurrentOrder();
        if (orderRes.data && orderRes.data._id && orderRes.data.status !== 'Delivered' && orderRes.data.status !== 'Cancelled') {
          setCurrentOrder(orderRes.data);
        } else {
          setCurrentOrder(null); // Clear if order is delivered or cancelled
        }
      } catch (err) {
        // No current order
        setCurrentOrder(null);
      }
    } catch (error) {
      console.error('Failed to load partner data:', error);
      handleLogout();
    }
  };

  const handleLogin = async (email: string, password: string) => {
    try {
      const response = await partnerAPI.login({ email, password });
      localStorage.setItem('partnerToken', response.data.token);
      setPartner(response.data);
      await loadPartnerData();
      alert('Login successful!');
    } catch (error: any) {
      alert('Login failed: ' + (error.response?.data?.message || 'Unknown error'));
    }
  };

  const handleOTPLogin = (token: string, partnerData: any) => {
    localStorage.setItem('partnerToken', token);
    setPartner(partnerData);
    loadPartnerData();
    alert('Login successful!');
  };

  const handleRegister = async (data: any) => {
    try {
      const response = await partnerAPI.register(data);
      localStorage.setItem('partnerToken', response.data.token);
      setPartner(response.data);
      await loadPartnerData();
      alert('Registration successful!');
    } catch (error: any) {
      alert('Registration failed: ' + (error.response?.data?.message || 'Unknown error'));
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('partnerToken');
    setPartner(null);
    setEarnings({});
    setAvailableOrders([]);
    setCurrentOrder(null);
    setView('login');
  };

  const handleToggleAvailability = async () => {
    try {
      const response = await partnerAPI.toggleAvailability();
      setPartner({ ...partner, isAvailable: response.data.isAvailable });
      alert(response.data.message);
    } catch (error: any) {
      alert('Failed to update availability');
    }
  };

  const handleViewOrders = async () => {
    try {
      const response = await partnerAPI.getAvailableOrders();
      setAvailableOrders(response.data);
      setView('orders');
    } catch (error) {
      alert('Failed to load orders');
    }
  };

  const handleAcceptOrder = async (orderId: string) => {
    try {
      const response = await partnerAPI.acceptOrder(orderId);
      setCurrentOrder(response.data.order);
      alert(response.data.message);
      setView('current-order');
      await loadPartnerData(); // Refresh data
    } catch (error: any) {
      alert('Failed to accept order: ' + (error.response?.data?.message || 'Unknown error'));
    }
  };

  const handleCancelOrder = async (orderId: string) => {
    const reason = prompt('Please provide a reason for cancellation:');
    if (!reason) return; // User cancelled the prompt
    
    if (window.confirm('Are you sure you want to cancel this order?')) {
      try {
        // Call API to cancel order
        await partnerAPI.rejectOrder(orderId, reason);
        alert('Order cancelled successfully');
        
        // Refresh available orders and update count
        const response = await partnerAPI.getAvailableOrders();
        setAvailableOrders(response.data);
        setAvailableOrdersCount(response.data.length);
      } catch (error: any) {
        alert('Failed to cancel order: ' + (error.response?.data?.message || 'Could not cancel order'));
      }
    }
  };

  const handleCall = (phone: string) => {
    window.open(`tel:${phone}`, '_self');
  };

  const handleChat = (orderId: string, userName: string, phone: string) => {
    setChatModal({ orderId, userName, phone });
  };

  const handleUpdateOrderStatus = async (status: string, data?: any) => {
    if (!currentOrder) return;

    try {
      const payload = data ? { ...data } : { status };
      const response = await partnerAPI.updateOrderStatusEnhanced(currentOrder._id, payload);
      
      if (status === 'Delivered') {
        alert('Order delivered! Earnings credited to your account.');
        setCurrentOrder(null);
        // Refresh partner data to clear activeOrder
        await loadPartnerData();
        // Also refresh available orders count
        try {
          const ordersResponse = await partnerAPI.getAvailableOrders();
          setAvailableOrdersCount(ordersResponse.data.length);
        } catch (err) {
          console.error('Failed to refresh orders:', err);
        }
        setView('dashboard');
      } else {
        // Update with response data to get OTP and other fields
        setCurrentOrder(response.data.order);
        const statusMsg = status === 'Items Packed' ? 'Bill sent to customer!' : `Order status updated to: ${status}`;
        alert(statusMsg);
      }
    } catch (error: any) {
      alert('Failed to update status: ' + (error.response?.data?.message || 'Unknown error'));
    }
  };

  const handleViewCurrentOrder = () => {
    if (currentOrder) {
      setView('current-order');
    } else {
      alert('No active order');
    }
  };

  // Make it globally accessible for Dashboard button
  (window as any).viewCurrentOrder = handleViewCurrentOrder;

  const handleViewHistory = async () => {
    try {
      const response = await partnerAPI.getDeliveryHistory();
      setDeliveryHistory(response.data);
      setView('history');
    } catch (error) {
      alert('Failed to load history');
    }
  };

  const handleViewProfile = () => {
    setView('profile');
  };

  // Show location permission screen first
  if (!locationGranted) {
    return <LocationPermission onLocationGranted={handleLocationGranted} />;
  }

  return (
    <div className="App">
      {view === 'login' && (
        <Login onLogin={handleLogin} onRegister={handleRegister} onOTPLogin={handleOTPLogin} />
      )}

      {view === 'dashboard' && partner && (
        <Dashboard
          partner={partner}
          earnings={earnings}
          availableOrdersCount={availableOrdersCount}
          onToggleAvailability={handleToggleAvailability}
          onViewOrders={handleViewOrders}
          onViewHistory={handleViewHistory}
          onViewProfile={handleViewProfile}
          onLogout={handleLogout}
        />
      )}

      {view === 'orders' && (
        <OrdersList
          orders={availableOrders}
          onAccept={handleAcceptOrder}
          onCancel={handleCancelOrder}
          onChat={handleChat}
          onCall={handleCall}
          onBack={async () => {
            // Refresh count before going back
            try {
              const response = await partnerAPI.getAvailableOrders();
              setAvailableOrdersCount(response.data.length);
            } catch (error) {
              console.error('Failed to refresh count:', error);
            }
            setView('dashboard');
          }}
        />
      )}

      {view === 'current-order' && (
        <CurrentOrder
          order={currentOrder}
          onUpdateStatus={handleUpdateOrderStatus}
          onBack={() => setView('dashboard')}
          onChat={handleChat}
        />
      )}

      {view === 'history' && (
        <DeliveryHistory
          orders={deliveryHistory}
          onBack={() => setView('dashboard')}
        />
      )}

      {view === 'profile' && partner && (
        <Profile
          partner={partner}
          onBack={() => setView('dashboard')}
          onUpdate={loadPartnerData}
        />
      )}

      {partner?.activeOrder && currentOrder && view === 'dashboard' && (
        <button
          onClick={handleViewCurrentOrder}
          style={{
            position: 'fixed',
            bottom: '20px',
            right: '20px',
            padding: '15px 25px',
            background: '#28a745',
            color: 'white',
            border: 'none',
            borderRadius: '50px',
            fontSize: '16px',
            fontWeight: 'bold',
            cursor: 'pointer',
            boxShadow: '0 4px 15px rgba(0,0,0,0.3)',
            zIndex: 1000,
          }}
        >
          🚴 View Current Order
        </button>
      )}

      {/* New Order Popup */}
      {newOrderPopup && view === 'dashboard' && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0, 0, 0, 0.7)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 2000,
          animation: 'fadeIn 0.3s'
        }}>
          <div style={{
            background: newOrderPopup.isMultiStoreOrder ? 'linear-gradient(135deg, #667eea, #764ba2)' : 'white',
            borderRadius: '20px',
            padding: '30px',
            maxWidth: '400px',
            width: '90%',
            boxShadow: '0 10px 40px rgba(0,0,0,0.3)',
            animation: 'slideUp 0.3s'
          }}>
            <div style={{textAlign: 'center', marginBottom: '20px'}}>
              <div style={{fontSize: '60px', marginBottom: '10px'}}>
                {newOrderPopup.isMultiStoreOrder ? '🏆' : '🔔'}
              </div>
              <h2 style={{
                margin: '0 0 10px', 
                color: newOrderPopup.isMultiStoreOrder ? '#fff' : '#fc8019', 
                fontSize: '24px'
              }}>
                {newOrderPopup.isMultiStoreOrder ? '🌟 Multi-Store Order!' : 'New Order Available!'}
              </h2>
              <p style={{
                margin: 0, 
                color: newOrderPopup.isMultiStoreOrder ? 'rgba(255,255,255,0.9)' : '#666', 
                fontSize: '16px'
              }}>
                {newOrderPopup.isMultiStoreOrder 
                  ? 'Earn MORE with smart multi-pickup!' 
                  : 'You have a new delivery request'}
              </p>
            </div>

            {/* Multi-store bonus banner */}
            {newOrderPopup.isMultiStoreOrder && (
              <div style={{
                background: '#fbbf24',
                color: '#78350f',
                padding: '12px 15px',
                borderRadius: '10px',
                marginBottom: '15px',
                textAlign: 'center',
                fontWeight: 'bold'
              }}>
                💰 BONUS: ₹70 for multi-store pickup!
              </div>
            )}

            <div style={{
              background: newOrderPopup.isMultiStoreOrder ? 'rgba(255,255,255,0.15)' : '#f9f9f9',
              padding: '20px',
              borderRadius: '12px',
              marginBottom: '20px'
            }}>
              <p style={{
                margin: '0 0 10px', 
                fontSize: '14px', 
                color: newOrderPopup.isMultiStoreOrder ? 'rgba(255,255,255,0.9)' : '#666'
              }}>
                <strong>Order #:</strong> {newOrderPopup.orderNumber || newOrderPopup._id?.slice(-6)}
              </p>
              <p style={{
                margin: '0 0 10px', 
                fontSize: '14px', 
                color: newOrderPopup.isMultiStoreOrder ? 'rgba(255,255,255,0.9)' : '#666'
              }}>
                <strong>Customer:</strong> {newOrderPopup.customerId?.name || newOrderPopup.user?.name || 'Customer'}
              </p>
              <p style={{
                margin: '0 0 10px', 
                fontSize: '14px', 
                color: newOrderPopup.isMultiStoreOrder ? 'rgba(255,255,255,0.9)' : '#666'
              }}>
                <strong>{newOrderPopup.isMultiStoreOrder ? 'Stores:' : 'Store:'}</strong> {
                  newOrderPopup.isMultiStoreOrder && newOrderPopup.storeNames
                    ? newOrderPopup.storeNames.join(', ')
                    : newOrderPopup.storeId?.name || newOrderPopup.store?.name || 'Store'
                }
              </p>
              <p style={{
                margin: '0 0 10px', 
                fontSize: '14px', 
                color: newOrderPopup.isMultiStoreOrder ? 'rgba(255,255,255,0.9)' : '#666'
              }}>
                <strong>Items:</strong> {newOrderPopup.items?.length || 0} items
              </p>
              <p style={{
                margin: 0, 
                fontSize: '20px', 
                fontWeight: 'bold', 
                color: newOrderPopup.isMultiStoreOrder ? '#fbbf24' : '#60b246'
              }}>
                Earnings: ₹{newOrderPopup.isMultiStoreOrder ? 70 : (newOrderPopup.deliveryCharges || 50)}
                {newOrderPopup.isMultiStoreOrder && (
                  <span style={{ fontSize: '12px', display: 'block', marginTop: '5px', color: 'rgba(255,255,255,0.8)' }}>
                    (₹20 extra for multi-pickup)
                  </span>
                )}
              </p>
            </div>

            <div style={{display: 'flex', gap: '10px'}}>
              <button
                onClick={() => setNewOrderPopup(null)}
                style={{
                  flex: 1,
                  padding: '14px',
                  background: newOrderPopup.isMultiStoreOrder ? 'rgba(255,255,255,0.2)' : '#e0e0e0',
                  color: newOrderPopup.isMultiStoreOrder ? 'white' : '#666',
                  border: 'none',
                  borderRadius: '10px',
                  fontSize: '16px',
                  fontWeight: 'bold',
                  cursor: 'pointer'
                }}
              >
                Close
              </button>
              <button
                onClick={() => {
                  setNewOrderPopup(null);
                  handleViewOrders();
                }}
                style={{
                  flex: 1,
                  padding: '14px',
                  background: newOrderPopup.isMultiStoreOrder ? '#fbbf24' : '#fc8019',
                  color: newOrderPopup.isMultiStoreOrder ? '#78350f' : 'white',
                  border: 'none',
                  borderRadius: '10px',
                  fontSize: '16px',
                  fontWeight: 'bold',
                  cursor: 'pointer'
                }}
              >
                {newOrderPopup.isMultiStoreOrder ? '🚀 Accept Now!' : 'View Orders'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Chat Modal */}
      {chatModal && (
        <ChatModal
          customerName={chatModal.userName}
          customerPhone={chatModal.phone}
          orderId={chatModal.orderId}
          onClose={() => setChatModal(null)}
          onCall={() => {
            handleCall(chatModal.phone);
            setChatModal(null);
          }}
        />
      )}
    </div>
  );
}

export default App;
